import React, { useState, useEffect } from 'react';
import { Plus, FileText, Download } from 'lucide-react';
import { consentService } from '../../../lib/services.ipd';
import type { ConsentTemplate, Consent, Admission } from '../../../lib/services.ipd';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

interface ConsentsTabProps {
  admissionId: string;
  admission: Admission | null;
}

export default function ConsentsTab({ admissionId, admission }: ConsentsTabProps) {
  const [consents, setConsents] = useState<Consent[]>([]);
  const [templates, setTemplates] = useState<ConsentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ConsentTemplate | null>(null);
  const [previewBody, setPreviewBody] = useState('');
  
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<{
    template_id: string;
    signed_by: string;
  }>();

  const templateId = watch('template_id');

  useEffect(() => {
    loadData();
  }, [admissionId]);

  useEffect(() => {
    if (templateId && admission) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setSelectedTemplate(template);
        const rendered = consentService.renderTemplate(template.body, {
          patient_name: admission.patient?.name || '',
          doctor_name: admission.doctor?.name || '',
          admission_no: admission.admission_no || '',
          admission_date: new Date(admission.admission_date).toLocaleDateString(),
          procedure_name: '',
        });
        setPreviewBody(rendered);
      }
    }
  }, [templateId, templates, admission]);

  const loadData = async () => {
    try {
      const [consentsData, templatesData] = await Promise.all([
        consentService.getByAdmission(admissionId),
        consentService.getTemplates(),
      ]);
      setConsents(consentsData);
      setTemplates(templatesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load consent data');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: { template_id: string; signed_by: string }) => {
    try {
      if (!selectedTemplate) return;
      
      await consentService.create({
        admission_id: admissionId,
        template_id: data.template_id,
        template_name: selectedTemplate.name,
        filled_body: previewBody,
        signed_by: data.signed_by,
        signed_at: new Date().toISOString(),
      });
      toast.success('Consent form saved successfully');
      reset();
      setShowConsentForm(false);
      setSelectedTemplate(null);
      setPreviewBody('');
      loadData();
    } catch (error) {
      console.error('Failed to save consent:', error);
      toast.error('Failed to save consent form');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Consent Forms</h3>
        <button
          onClick={() => {
            setShowConsentForm(!showConsentForm);
            reset();
            setSelectedTemplate(null);
            setPreviewBody('');
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          New Consent
        </button>
      </div>

      {showConsentForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Consent Template <span className="text-red-500">*</span>
            </label>
            <select
              {...register('template_id', { required: 'Template is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select template</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
            {errors.template_id && <p className="text-sm text-red-600 mt-1">{errors.template_id.message}</p>}
          </div>

          {previewBody && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
              <div className="bg-white p-4 border border-gray-300 rounded-lg whitespace-pre-wrap text-sm">
                {previewBody}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Patient/Guardian Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('signed_by', { required: 'Signature name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter patient or guardian name"
            />
            {errors.signed_by && <p className="text-sm text-red-600 mt-1">{errors.signed_by.message}</p>}
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Consent
            </button>
            <button
              type="button"
              onClick={() => {
                setShowConsentForm(false);
                reset();
                setSelectedTemplate(null);
                setPreviewBody('');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {consents.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No consent forms recorded</p>
        ) : (
          consents.map((consent) => (
            <div key={consent.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-900">{consent.template_name}</span>
                </div>
                <span className="text-sm text-gray-500">
                  {new Date(consent.signed_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-gray-600">Signed by: {consent.signed_by}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
